import {
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  ElementRef,
  computed,
  effect,
  inject,
  input,
  output,
  signal,
  untracked,
  viewChild,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormControl, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { QuillModule } from 'ngx-quill';
import { VerifyDataViolation } from '../../../core/models/organization.models';
import { ValidationDraft, ValidationEmailPayload } from '../../models/validation-dialog.models';
import {
  extractRichTextPlainText,
  richTextRequiredValidator,
} from '../../utils/notification-dialog.validators';
import { ComparisonLabelPipe } from '../comparison-dialog/comparison-label.pipe';

@Component({
  selector: 'app-validation-dialog',
  imports: [ReactiveFormsModule, QuillModule, ComparisonLabelPipe],
  templateUrl: './validation-dialog.html',
  styleUrl: './validation-dialog.css',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ValidationDialog {
  private readonly destroyRef = inject(DestroyRef);
  private customDraftSequence = 0;
  private syncedDraftId: string | null = null;
  private readonly tabListRef = viewChild<ElementRef<HTMLDivElement>>('tabsScroller');

  readonly isOpen = input(false);
  readonly violations = input<VerifyDataViolation[]>([]);
  readonly tipoDocumento = input<string | null>(null);
  readonly isLoading = input(false);

  readonly closeDialog = output<void>();
  readonly sendValidation = output<ValidationEmailPayload>();

  protected readonly selectedIndexes = signal<Set<number>>(new Set());
  protected readonly emailDrafts = signal<ValidationDraft[]>([]);
  protected readonly activeDraftId = signal<string | null>(null);
  protected readonly showValidationErrors = signal(false);
  protected readonly validationForm = new FormGroup({
    subject: new FormControl('', {
      nonNullable: true,
      validators: [Validators.required],
    }),
    body: new FormControl('', {
      nonNullable: true,
      validators: [richTextRequiredValidator],
    }),
  });
  protected readonly subjectControl = this.validationForm.controls.subject;
  protected readonly bodyControl = this.validationForm.controls.body;

  protected readonly messageEditorModules = {
    toolbar: [
      ['bold', 'italic', 'underline'],
      [{ list: 'ordered' }, { list: 'bullet' }],
      ['link'],
      [
        { background: ['orange', '#f97316', '#f472b6', '#38bdf8', '#34d399'] },
        { color: ['red', '#ef4444', '#3b82f6', '#22c55e', '#f59e0b', '#6b7280'] },
      ],
    ],
  };

  protected readonly hasViolations = computed(() => this.violations().length > 0);
  protected readonly draftCount = computed(() => this.emailDrafts().length);
  protected readonly activeDraft = computed<ValidationDraft | null>(() => {
    const activeDraftId = this.activeDraftId();
    return this.emailDrafts().find((draft) => draft.id === activeDraftId) ?? null;
  });
  protected readonly invalidDraftIds = computed(() => {
    const invalidIds = new Set<string>();

    for (const draft of this.emailDrafts()) {
      if (!this.isDraftValid(draft)) {
        invalidIds.add(draft.id);
      }
    }

    return invalidIds;
  });
  protected readonly canSubmit = computed(() => !this.isLoading() && this.draftCount() > 0);

  constructor() {
    effect(() => {
      if (!this.isOpen()) {
        this.selectedIndexes.set(new Set());
        this.emailDrafts.set([]);
        this.activeDraftId.set(null);
        this.showValidationErrors.set(false);
        this.validationForm.reset({ subject: '', body: '' }, { emitEvent: false });
        this.validationForm.markAsPristine();
        this.validationForm.markAsUntouched();
      }
    });

    effect(() => {
      if (this.isLoading() || !this.activeDraft()) {
        this.validationForm.disable({ emitEvent: false });
        return;
      }

      this.validationForm.enable({ emitEvent: false });
    });

    effect(() => {
      const selectedIndexes = this.selectedIndexes();
      const violations = this.violations();
      const currentDrafts = untracked(() => this.emailDrafts());
      const currentActiveDraftId = untracked(() => this.activeDraftId());
      const violationDrafts = violations.flatMap((violation, index) => {
        if (!selectedIndexes.has(index)) {
          return [];
        }

        const draftId = this.getViolationDraftId(index);
        const existingDraft = currentDrafts.find((draft) => draft.id === draftId);
        return [existingDraft ?? this.createViolationDraft(violation, index)];
      });
      const customDrafts = currentDrafts.filter((draft) => draft.kind === 'custom');
      const nextDrafts = [...violationDrafts, ...customDrafts];
      const nextActiveDraftId =
        currentActiveDraftId && nextDrafts.some((draft) => draft.id === currentActiveDraftId)
          ? currentActiveDraftId
          : (nextDrafts[0]?.id ?? null);

      this.emailDrafts.set(nextDrafts);
      this.activeDraftId.set(nextActiveDraftId);
    });

    effect(() => {
      const activeDraft = this.activeDraft();
      const currentValue = this.validationForm.getRawValue();
      const nextDraftId = activeDraft?.id ?? null;
      const hasDraftChanged = this.syncedDraftId !== nextDraftId;

      if (activeDraft) {
        const shouldSyncValues =
          hasDraftChanged ||
          currentValue.subject !== activeDraft.subject ||
          currentValue.body !== activeDraft.body;

        if (!shouldSyncValues) {
          return;
        }

        this.validationForm.patchValue(
          { subject: activeDraft.subject, body: activeDraft.body },
          { emitEvent: false },
        );
      } else {
        const shouldReset =
          hasDraftChanged || currentValue.subject !== '' || currentValue.body !== '';

        if (!shouldReset) {
          return;
        }

        this.validationForm.reset({ subject: '', body: '' }, { emitEvent: false });
      }

      this.syncedDraftId = nextDraftId;
      this.validationForm.markAsPristine();
      if (this.showValidationErrors() && activeDraft) {
        this.validationForm.markAllAsTouched();
      } else {
        this.validationForm.markAsUntouched();
      }
    });

    this.validationForm.valueChanges
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((value) => {
        const activeDraftId = this.activeDraftId();

        if (!activeDraftId) {
          return;
        }

        this.emailDrafts.update((drafts) =>
          drafts.map((draft) =>
            draft.id === activeDraftId
              ? {
                  ...draft,
                  subject: value.subject ?? '',
                  body: value.body ?? '',
                }
              : draft,
          ),
        );
      });
  }

  private buildSingleBody(violation: VerifyDataViolation): string {
    return violation.body ? `<p>${violation.body}</p>` : '';
  }

  private getViolationDraftId(index: number): string {
    return `violation-${index}`;
  }

  private createViolationDraft(violation: VerifyDataViolation, index: number): ValidationDraft {
    return {
      id: this.getViolationDraftId(index),
      kind: 'violation',
      sourceIndex: index,
      ruleIds: [violation.rule_id],
      sectionDocType: violation.document_type,
      sectionDocSubtype: violation.document_subtype,
      defaultTitle: violation.subject || `Violazione ${index + 1}`,
      subject: violation.subject,
      body: this.buildSingleBody(violation),
    };
  }

  private getDefaultSectionDocType(): string {
    return this.activeDraft()?.sectionDocType || this.violations()[0]?.document_type || '';
  }

  private getDefaultSectionDocSubtype(): string {
    return this.activeDraft()?.sectionDocSubtype || this.violations()[0]?.document_subtype || '';
  }

  private createCustomDraft(): ValidationDraft {
    this.customDraftSequence += 1;

    return {
      id: `custom-${this.customDraftSequence}`,
      kind: 'custom',
      sourceIndex: null,
      ruleIds: [],
      sectionDocType: this.getDefaultSectionDocType(),
      sectionDocSubtype: this.getDefaultSectionDocSubtype(),
      defaultTitle: `Nuovo messaggio ${this.customDraftSequence}`,
      subject: '',
      body: '',
    };
  }

  private isDraftValid(draft: Pick<ValidationDraft, 'subject' | 'body'>): boolean {
    return draft.subject.trim().length > 0 && extractRichTextPlainText(draft.body).length > 0;
  }

  protected readonly isAllSelected = computed(() => {
    const list = this.violations();
    return list.length > 0 && this.selectedIndexes().size === list.length;
  });

  protected readonly isSomeSelected = computed(() => {
    const size = this.selectedIndexes().size;
    return size > 0 && size < this.violations().length;
  });

  protected readonly selectedCount = computed(() => this.selectedIndexes().size);

  toggleSelection(index: number) {
    const current = new Set(this.selectedIndexes());

    if (current.has(index)) {
      current.delete(index);
    } else {
      current.add(index);
      this.activeDraftId.set(this.getViolationDraftId(index));
    }

    this.selectedIndexes.set(current);
  }

  toggleAll() {
    if (this.isAllSelected()) {
      this.selectedIndexes.set(new Set());
      return;
    }

    this.selectedIndexes.set(new Set(this.violations().map((_, index) => index)));
    if (this.violations().length > 0) {
      this.activeDraftId.set(this.getViolationDraftId(0));
    }
  }

  selectDraft(draftId: string) {
    this.activeDraftId.set(draftId);
    this.scrollDraftIntoView(draftId);
  }

  addCustomDraft() {
    const draft = this.createCustomDraft();

    this.emailDrafts.update((drafts) => [...drafts, draft]);
    this.activeDraftId.set(draft.id);
    this.scrollDraftIntoView(draft.id);
  }

  closeDraft(draftId: string) {
    if (this.isLoading()) {
      return;
    }

    const draft = this.emailDrafts().find((item) => item.id === draftId);

    if (!draft) {
      return;
    }

    if (draft.kind === 'violation' && draft.sourceIndex !== null) {
      const nextSelection = new Set(this.selectedIndexes());
      nextSelection.delete(draft.sourceIndex);
      this.selectedIndexes.set(nextSelection);
      return;
    }

    const remainingDrafts = this.emailDrafts().filter((item) => item.id !== draftId);
    this.emailDrafts.set(remainingDrafts);

    if (this.activeDraftId() === draftId) {
      this.activeDraftId.set(remainingDrafts[0]?.id ?? null);
    }
  }

  isDraftActive(draftId: string): boolean {
    return this.activeDraftId() === draftId;
  }

  isDraftInvalid(draftId: string): boolean {
    return this.showValidationErrors() && this.invalidDraftIds().has(draftId);
  }

  getDraftTabLabel(draft: ValidationDraft): string {
    return draft.subject.trim() || draft.defaultTitle;
  }

  scrollTabs(direction: 'left' | 'right') {
    const tabList = this.tabListRef()?.nativeElement;

    if (!tabList) {
      return;
    }

    tabList.scrollBy({
      left: direction === 'left' ? -180 : 180,
      behavior: 'smooth',
    });
  }

  private scrollDraftIntoView(draftId: string) {
    queueMicrotask(() => {
      const tab = document.getElementById(`validation-draft-tab-${draftId}`);
      tab?.scrollIntoView({ inline: 'nearest', block: 'nearest', behavior: 'smooth' });
    });
  }

  requestClose() {
    if (this.isLoading()) {
      return;
    }

    this.closeDialog.emit();
  }

  submit() {
    if (this.isLoading() || this.emailDrafts().length === 0) {
      return;
    }

    const firstInvalidDraft = this.emailDrafts().find((draft) => !this.isDraftValid(draft));

    if (firstInvalidDraft) {
      this.showValidationErrors.set(true);
      this.activeDraftId.set(firstInvalidDraft.id);
      return;
    }

    this.sendValidation.emit({
      messages: this.emailDrafts().map((draft) => ({
        ruleIds: [...draft.ruleIds],
        sectionDocType: draft.sectionDocType,
        sectionDocSubtype: draft.sectionDocSubtype,
        subject: draft.subject,
        body: draft.body,
      })),
    });
  }
}
